/**
 * Combobox — searchable single-select on Base UI <Combobox>.
 *
 * Design system mapping:
 *   • Trigger: matches Input (mch-input)
 *   • Popup: panel/popover style — bg-white dark:bg-webex-gray-950
 *            border webex-gray-300 dark:border-webex-gray-700
 *            rounded-lg shadow-overlay
 *   • Items:  px-3 py-1.5 text-sm
 *            hover/highlight: bg webex-gray-100 dark: webex-gray-850
 *            selected: bg webex-blue-100 dark:bg-webex-blue-900 text-webex-blue-700 dark:text-webex-blue-400
 *
 * Usage:
 *   <Combobox items={[{value:"a", label:"Apple"}, ...]} placeholder="Search…" />
 */
import * as React from "react"
import { Combobox as BaseCombobox } from "@base-ui-components/react/combobox"
import { cn } from "@lib/cn"
import { inputVariants } from "./Input"

export interface ComboboxItem {
  value: string
  label: string
  disabled?: boolean
}

export interface ComboboxProps {
  items: ComboboxItem[]
  value?: string | null
  defaultValue?: string | null
  onValueChange?: (value: string | null) => void
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  name?: string
  className?: string
  inputClassName?: string
  popupClassName?: string
  state?: "default" | "error"
}

export function Combobox({
  items,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Search…",
  emptyMessage = "No results.",
  disabled,
  name,
  className,
  inputClassName,
  popupClassName,
  state = "default",
}: ComboboxProps) {
  // Base UI's Combobox generic infers the selection type from `items`
  // (so it becomes ComboboxItem), but our external API exposes the string
  // `value` instead. The `as never` casts reconcile that gap; swap to an
  // explicit generic if Base UI exposes one in a stable release.
  return (
    <BaseCombobox.Root
      items={items}
      value={value as never}
      defaultValue={defaultValue as never}
      onValueChange={onValueChange as never}
      itemToStringLabel={(item: ComboboxItem) => item.label}
      itemToStringValue={(item: ComboboxItem) => item.value}
      disabled={disabled}
      name={name}
    >
      <span className={cn("relative inline-block w-full", className)}>
        <BaseCombobox.Input
          placeholder={placeholder}
          className={cn(inputVariants({ state }), "pr-8", inputClassName)}
        />
        <BaseCombobox.Icon
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-webex-gray-600 dark:text-webex-gray-400"
        >
          <svg viewBox="0 0 14 14" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3,5.5 7,9.5 11,5.5" />
          </svg>
        </BaseCombobox.Icon>
      </span>
      <BaseCombobox.Portal>
        <BaseCombobox.Positioner sideOffset={4} className="z-50">
          <BaseCombobox.Popup
            className={cn(
              "max-h-[16rem] overflow-y-auto rounded-md border bg-white dark:bg-webex-gray-950",
              "border-webex-gray-300 dark:border-webex-gray-700",
              "shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.6)]",
              "py-1 outline-none",
              "min-w-[var(--anchor-width)]",
              popupClassName,
            )}
          >
            <BaseCombobox.Empty className="px-3 py-2 text-xs text-webex-gray-600 dark:text-webex-gray-400">
              {emptyMessage}
            </BaseCombobox.Empty>
            <BaseCombobox.List>
              {(item: ComboboxItem) => (
                <BaseCombobox.Item
                  key={item.value}
                  value={item}
                  disabled={item.disabled}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm",
                    "text-webex-gray-1000 dark:text-white",
                    "data-[highlighted]:bg-webex-gray-100 dark:data-[highlighted]:bg-webex-gray-850",
                    "data-[selected]:bg-webex-blue-100 data-[selected]:text-webex-blue-700",
                    "dark:data-[selected]:bg-webex-blue-900 dark:data-[selected]:text-webex-blue-400",
                    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
                    "outline-none",
                  )}
                >
                  {item.label}
                </BaseCombobox.Item>
              )}
            </BaseCombobox.List>
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  )
}
